<script setup lang="ts">
import { computed, ref } from 'vue'
import type { SceneBackgroundImageFit } from '../utils/sceneBackgroundTexture'
import SceneGroupCard from './SceneGroupCard.vue'

export type SceneBackgroundMode = 'color' | 'image'

const props = withDefaults(
  defineProps<{
    mode: SceneBackgroundMode
    color: string
    imageName?: string
    imageLoading?: boolean
    message?: string
    imageFit?: SceneBackgroundImageFit
    imageZoom?: number
    imageOffsetX?: number
    imageOffsetY?: number
    imageRotation?: number
    imageFillColor?: string
  }>(),
  {
    imageName: '',
    imageLoading: false,
    message: '',
    imageFit: 'cover',
    imageZoom: 1,
    imageOffsetX: 0,
    imageOffsetY: 0,
    imageRotation: 0,
    imageFillColor: '#000000',
  },
)

const emit = defineEmits<{
  'update:mode': [mode: SceneBackgroundMode]
  'update:color': [color: string]
  'select-image': [file: File]
  'remove-image': []
  'update:image-fit': [fit: SceneBackgroundImageFit]
  'update:image-zoom': [zoom: number]
  'update:image-offset-x': [offset: number]
  'update:image-offset-y': [offset: number]
  'update:image-rotation': [rotation: number]
  'update:image-fill-color': [color: string]
  'reset-image-transform': []
  reset: []
}>()

const fileInputRef = ref<HTMLInputElement | null>(null)
const hasImage = computed(() => props.imageName.length > 0)
const badge = computed(() => {
  if (props.imageLoading) return '加载中'
  if (props.mode === 'image') return hasImage.value ? '图片' : '待选择'
  return '纯色'
})

function openImagePicker(): void {
  if (!props.imageLoading) {
    fileInputRef.value?.click()
  }
}

function handleImageInput(event: Event): void {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (file) {
    emit('select-image', file)
  }
}

function handleColorInput(event: Event): void {
  emit('update:color', (event.target as HTMLInputElement).value)
}

function getNumericInputValue(event: Event, fallback: number): number {
  const value = Number((event.target as HTMLInputElement).value)
  return Number.isFinite(value) ? value : fallback
}
</script>

<template>
  <SceneGroupCard
    title="场景背景"
    subtitle="设置 Three.js 场景的纯色或图片背景"
    :badge="badge"
  >
    <div class="background-card">
      <div class="background-mode-grid">
        <button
          class="panel-button"
          :class="{ active: mode === 'color' }"
          type="button"
          :aria-pressed="mode === 'color'"
          @click="emit('update:mode', 'color')"
        >
          <span class="panel-button-label">纯色</span>
          <span class="panel-button-meta">THREE.Color</span>
        </button>
        <button
          class="panel-button"
          :class="{ active: mode === 'image' }"
          type="button"
          :aria-pressed="mode === 'image'"
          @click="emit('update:mode', 'image')"
        >
          <span class="panel-button-label">图片</span>
          <span class="panel-button-meta">THREE.Texture</span>
        </button>
      </div>

      <label v-if="mode === 'color'" class="background-color-row">
        <span class="background-field-label">背景颜色</span>
        <input
          class="background-color-input"
          type="color"
          :value="color"
          @input="handleColorInput"
        />
        <span class="background-color-value">{{ color.toUpperCase() }}</span>
      </label>

      <div v-else class="background-image-editor">
        <input
          ref="fileInputRef"
          class="background-file-input"
          type="file"
          accept="image/*"
          @change="handleImageInput"
        />
        <button
          class="panel-button panel-button-subtle"
          type="button"
          :disabled="imageLoading"
          @click="openImagePicker"
        >
          {{ hasImage ? '更换背景图片' : '选择背景图片' }}
        </button>
        <div class="background-image-name" :title="imageName">
          {{ imageName || '支持 PNG、JPEG、WebP 等浏览器可解码图片' }}
        </div>
        <div v-if="hasImage" class="background-image-settings">
          <label class="background-select-row">
            <span class="background-field-label">适配方式</span>
            <select
              class="panel-input background-fit-select"
              :value="imageFit"
              @change="emit('update:image-fit', ($event.target as HTMLSelectElement).value as SceneBackgroundImageFit)"
            >
              <option value="cover">铺满裁剪（推荐）</option>
              <option value="contain">完整显示</option>
              <option value="stretch">拉伸铺满</option>
            </select>
          </label>

          <label v-if="imageFit === 'contain'" class="background-color-row">
            <span class="background-field-label">留边颜色</span>
            <input
              class="background-color-input"
              type="color"
              :value="imageFillColor"
              @input="emit('update:image-fill-color', ($event.target as HTMLInputElement).value)"
            />
            <span class="background-color-value">{{ imageFillColor.toUpperCase() }}</span>
          </label>

          <label class="background-range-row">
            <span class="background-field-label">缩放倍率</span>
            <strong>{{ imageZoom.toFixed(2) }}×</strong>
            <input
              class="background-range-input"
              type="range"
              min="1"
              max="3"
              step="0.01"
              :value="imageZoom"
              @input="emit('update:image-zoom', getNumericInputValue($event, imageZoom))"
            />
            <input
              class="panel-input background-number-input"
              type="number"
              min="1"
              max="3"
              step="0.01"
              :value="imageZoom"
              @change="emit('update:image-zoom', getNumericInputValue($event, imageZoom))"
            />
          </label>

          <label class="background-range-row">
            <span class="background-field-label">水平焦点</span>
            <strong>{{ Math.round(imageOffsetX * 100) }}%</strong>
            <input
              class="background-range-input"
              type="range"
              min="-1"
              max="1"
              step="0.01"
              :value="imageOffsetX"
              @input="emit('update:image-offset-x', getNumericInputValue($event, imageOffsetX))"
            />
            <input
              class="panel-input background-number-input"
              type="number"
              min="-1"
              max="1"
              step="0.01"
              :value="imageOffsetX"
              @change="emit('update:image-offset-x', getNumericInputValue($event, imageOffsetX))"
            />
          </label>

          <label class="background-range-row">
            <span class="background-field-label">垂直焦点</span>
            <strong>{{ Math.round(imageOffsetY * 100) }}%</strong>
            <input
              class="background-range-input"
              type="range"
              min="-1"
              max="1"
              step="0.01"
              :value="imageOffsetY"
              @input="emit('update:image-offset-y', getNumericInputValue($event, imageOffsetY))"
            />
            <input
              class="panel-input background-number-input"
              type="number"
              min="-1"
              max="1"
              step="0.01"
              :value="imageOffsetY"
              @change="emit('update:image-offset-y', getNumericInputValue($event, imageOffsetY))"
            />
          </label>

          <label class="background-range-row">
            <span class="background-field-label">旋转角度</span>
            <strong>{{ Math.round(imageRotation) }}°</strong>
            <input
              class="background-range-input"
              type="range"
              min="-180"
              max="180"
              step="1"
              :value="imageRotation"
              @input="emit('update:image-rotation', getNumericInputValue($event, imageRotation))"
            />
            <input
              class="panel-input background-number-input"
              type="number"
              min="-180"
              max="180"
              step="1"
              :value="imageRotation"
              @change="emit('update:image-rotation', getNumericInputValue($event, imageRotation))"
            />
          </label>

          <button
            class="panel-button panel-button-subtle"
            type="button"
            @click="emit('reset-image-transform')"
          >
            重置图片构图
          </button>
        </div>
        <button
          v-if="hasImage"
          class="panel-button panel-button-subtle"
          type="button"
          :disabled="imageLoading"
          @click="emit('remove-image')"
        >
          移除图片
        </button>
      </div>

      <p v-if="message" class="background-message">{{ message }}</p>

      <button
        class="panel-button panel-button-subtle background-reset-button"
        type="button"
        :disabled="imageLoading"
        @click="emit('reset')"
      >
        恢复默认黑色
      </button>
    </div>
  </SceneGroupCard>
</template>

<style scoped>
.background-card,
.background-image-editor,
.background-image-settings {
  display: grid;
  gap: 10px;
}

.background-mode-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.background-color-row {
  min-height: 38px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 44px auto;
  align-items: center;
  gap: 10px;
}

.background-select-row {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
}

.background-fit-select {
  min-width: 0;
}

.background-field-label {
  color: #c7d5e6;
  font-size: 12px;
  font-weight: 800;
}

.background-color-input {
  width: 44px;
  height: 32px;
  padding: 2px;
  border: 1px solid rgba(166, 203, 226, 0.28);
  border-radius: 7px;
  background: rgba(1, 8, 15, 0.64);
  cursor: pointer;
}

.background-color-value,
.background-image-name,
.background-message {
  color: #9fb0c6;
  font-size: 11px;
}

.background-color-value {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.background-file-input {
  display: none;
}

.background-image-settings {
  padding: 10px;
  border: 1px solid rgba(160, 209, 238, 0.16);
  border-radius: 9px;
  background: rgba(3, 11, 19, 0.42);
}

.background-range-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 6px 10px;
}

.background-range-row strong {
  color: #dceaf4;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}

.background-range-input {
  min-width: 0;
  accent-color: #43dcc4;
}

.background-number-input {
  width: 72px;
  height: 32px;
  padding: 0 7px;
  font-size: 11px;
}

.background-image-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.background-message {
  margin: 0;
  line-height: 1.5;
}

.background-reset-button {
  width: 100%;
}
</style>
